import { CardSetManageStore } from '../stores/cardSetManage';
import { makeSelectList } from './catalogs';
import { addNotification } from './ui';
import dayjs from 'dayjs';
import { IBodyListResponse, IGetApiResponse, IRequestCardSetReassign, apiManager } from '../REST';
import {
    IAssetAssignment,
    IAssetAssignmentAttributesAPI,
    IAssetAssignmentsList,
    IAssignmentScope,
    ICardSetOwnerCompany,
    ICardSetOwnerCompanyAPI,
    ICardSetReassignRow,
    IItemAPI,
    IPatchItem,
    NotificationType,
} from '../interfaces';
import { ITrNotificationMessage, tNM } from '../translate';

export const CARD_SET_MANAGE_NEW_CARD_SET = 'newCardSet';
export const CARD_SET_MANAGE_PAGE_SIZE = 5;
export const CARD_SET_MANAGE_EFFECTIVE_DATE_NOW = 'now';
export function cleanCardSetManageStore(): void {
    const StoreInstance = CardSetManageStore.getInstance();
    StoreInstance.cleanStore();
}

// ------------ Owner companies ------------

export async function getCardSetOwnerCompanies(id: string): Promise<void> {
    const { resp, isError } = await apiManager.getApi('GET_CARD_SET_OWNER_COMPANIES', { id });
    if (isError) {
        return;
    }
    const list: ICardSetOwnerCompany[] = ((resp as unknown as ICardSetOwnerCompanyAPI[]) || []).map((i) => ({
        companyId: i.company_id ? `${i.company_id}` : '',
        company: i.company,
        cardsCount: i.cards_count,
    }));
    CardSetManageStore.getInstance().setOwnerCompanies(list);
}

// ------------ Disable checks ------------

const getRecordsCount = async (request: Promise<IGetApiResponse>): Promise<number> => {
    const { resp, isError } = await request;
    return isError ? 0 : resp?.meta?.records_count || 0;
};

export async function getCardSetManageChecks(id: string, companyId: string): Promise<void> {
    const [childrenResp, cardsCount] = await Promise.all([
        apiManager.getApi('GET_CARD_SET_MANAGE_COMPANIES_CHILDREN', { id: companyId }),
        getRecordsCount(
            apiManager.getApi('GET_CARD_SET_MANAGE_CARDS', { id, searchParamsList: { pageNumber: 1, pageSize: 1 } }),
        ),
        getCardSetOwnerCompanies(id),
    ]);

    const StoreInstance = CardSetManageStore.getInstance();
    StoreInstance.setChildrenSelect(makeSelectList(childrenResp.resp as IBodyListResponse));
    StoreInstance.setChecks({
        childrenCount: (childrenResp.resp as IBodyListResponse)?.data?.length || 0,
        cardsCount,
        assignedCompaniesCount: StoreInstance.ownerCompanies.filter((i) => i.companyId).length,
    });
}

// ------------ Blocking assignments ------------

const makeAssetAssignment = (i: IItemAPI): IAssetAssignment => {
    const attributes = (i.attributes || {}) as IAssetAssignmentAttributesAPI;
    return {
        id: i.id,
        assetId: attributes.asset_id ? `${attributes.asset_id}` : undefined,
        assetType: attributes.asset_type,
        assetName: attributes.asset_name || undefined,
        companyId: attributes.company_id ? `${attributes.company_id}` : undefined,
        companyName: attributes.company_name,
        startDate: attributes.start_date ? dayjs(attributes.start_date) : undefined,
        endDate: attributes.end_date ? dayjs(attributes.end_date) : undefined,
    };
};

export function cleanCardSetAssignments(): void {
    CardSetManageStore.getInstance().cleanAssignments();
}

export async function getCardSetAssignments(id: string, scope: IAssignmentScope, pageNumber = 1): Promise<void> {
    const apiType =
        scope === 'future' ? 'GET_ASSET_ASSIGNMENTS_FUTURE_LIST' : 'GET_ASSET_ASSIGNMENTS_PARENT_BLOCKED_LIST';
    const { resp, isError } = await apiManager.getApi(apiType, {
        searchParamsList: {
            cardSetId: id,
            assignmentScope: scope,
            pageNumber,
            pageSize: CARD_SET_MANAGE_PAGE_SIZE,
        },
    });
    const StoreInstance = CardSetManageStore.getInstance();
    const setList = (list: IAssetAssignmentsList): void =>
        scope === 'future' ? StoreInstance.setFutureAssignments(list) : StoreInstance.setParentAssignments(list);
    const current = scope === 'future' ? StoreInstance.futureAssignments : StoreInstance.parentAssignments;
    if (isError) {
        setList({ ...current, isLoaded: false });
        return;
    }
    const { data, meta } = (resp || {}) as IBodyListResponse;
    // An aborted request resolves without data; the newer request of the same apiType fills the store
    if (!data) {
        return;
    }
    if (!data.length && pageNumber > 1) {
        return getCardSetAssignments(id, scope, pageNumber - 1);
    }
    setList({ list: data.map(makeAssetAssignment), total: meta?.records_count || 0, pageNumber, isLoaded: true });
}

const notifySuccess = (message: ITrNotificationMessage): void => {
    addNotification({ type: NotificationType.SUCCESS, message: tNM(message), isTranslated: true });
};

export async function cancelAssetAssignment(id: string): Promise<boolean> {
    const { isError } = await apiManager.getApi('POST_ASSET_ASSIGNMENT_CANCEL', { id });
    !isError && notifySuccess('Assignment has been canceled');
    return !isError;
}

// ------------ Reassign ------------

export async function getCardSetManageCardSetsOptions(companyId: string): Promise<void> {
    const { resp, isError } = await apiManager.getApi('GET_CARD_SETS_SELECT', { searchParamsList: { companyId } });
    if (!isError) {
        CardSetManageStore.getInstance().setCardSetsOptions(companyId, makeSelectList(resp as IBodyListResponse, true));
    }
}

const sendCardSetReassign = async (
    id: string,
    body: IRequestCardSetReassign,
    successMessage: ITrNotificationMessage,
): Promise<IPatchItem> => {
    const {
        resp: { errors },
        isError,
    } = await apiManager.getApi('POST_CARD_SET_REASSIGN', { id }, body);
    !isError && notifySuccess(successMessage);
    return { isError, errors };
};

export async function sendCardSetReassignAll(
    id: string,
    row: ICardSetReassignRow,
    isStartNow?: boolean,
): Promise<IPatchItem> {
    const body: IRequestCardSetReassign = {
        target_company_id: row.companyId,
        effective_date: isStartNow ? CARD_SET_MANAGE_EFFECTIVE_DATE_NOW : row.startDate?.toISOString(),
    };
    if (row.targetCardSetId === CARD_SET_MANAGE_NEW_CARD_SET) {
        body.create_card_set = true;
    } else if (row.targetCardSetId) {
        body.target_card_set_id = row.targetCardSetId;
    }
    return sendCardSetReassign(id, body, 'Cards have been reassigned');
}

export async function sendCardSetCancelAll(id: string, companyId: string): Promise<IPatchItem> {
    return sendCardSetReassign(
        id,
        { target_company_id: companyId, effective_date: CARD_SET_MANAGE_EFFECTIVE_DATE_NOW },
        'Cards have been returned to the card set company',
    );
}

export async function refreshCardSetManage(id: string, companyId: string): Promise<void> {
    const StoreInstance = CardSetManageStore.getInstance();
    await Promise.all([
        getCardSetManageChecks(id, companyId),
        getCardSetAssignments(id, 'future', StoreInstance.futureAssignments.pageNumber),
        getCardSetAssignments(id, 'parent_blocked', StoreInstance.parentAssignments.pageNumber),
    ]);
}
